import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import { apiClient } from "./_client";

/** Per-sheet layout captured at ingest. Widths are Excel character units,
 * heights are points; `null` for sheets parsed before layout capture. */
export interface SpreadsheetSheetLayout {
  col_widths: Record<string, number>;
  row_heights: Record<string, number>;
  default_col_width: number | null;
  default_row_height: number | null;
  hidden_cols: number[];
  hidden_rows: number[];
  /** [min_row, min_col, max_row, max_col] */
  merges: [number, number, number, number][];
  frozen_rows: number;
  frozen_cols: number;
}

export interface SpreadsheetCellStyle {
  b?: boolean;
  bg?: string; // fill RGB hex, no "#"
  fc?: string; // font color RGB hex, no "#"
  ha?: "left" | "center" | "right";
}

export interface SpreadsheetSheetMeta {
  sheet_name: string;
  sheet_index: number;
  sheet_state: string; // "visible" | "hidden" | "veryHidden"
  cell_count: number;
  max_row: number;
  max_col: number;
  layout: SpreadsheetSheetLayout | null;
}

export interface SpreadsheetMeta {
  sheets: SpreadsheetSheetMeta[];
  values_uncomputed: boolean;
}

export interface SpreadsheetCellData {
  row: number;
  col: number;
  value: string | null;
  formula: string | null;
  is_header: boolean;
  data_type: string; // openpyxl types: n/s/d/b/e/f/str
  style: SpreadsheetCellStyle | null;
}

export interface SpreadsheetSheetData {
  sheet_name: string;
  cells: SpreadsheetCellData[];
}

/**
 * Sheet metadata for a document's natively-parsed spreadsheet.
 * An empty `sheets` list means the document has no parsed spreadsheet data
 * (PDFs, or XLSX ingested before the native path) — callers use this as the
 * "render the spreadsheet viewer?" probe.
 */
export const useSpreadsheetMeta = (
  documentId: string,
): UseQueryResult<SpreadsheetMeta> =>
  useQuery<SpreadsheetMeta>({
    enabled: !!documentId,
    queryKey: ["spreadsheet-meta", documentId],
    queryFn: async () => {
      const response = await apiClient.get<never, AxiosResponse<SpreadsheetMeta>>(
        `api/documents/${documentId}/spreadsheet/`,
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // cells are immutable per ingest
  });

/** Sparse cells for one sheet, ordered by (row, col). Fetched lazily per tab. */
export const useSpreadsheetSheet = (
  documentId: string,
  sheetName: string | null,
): UseQueryResult<SpreadsheetSheetData> =>
  useQuery<SpreadsheetSheetData>({
    enabled: !!documentId && !!sheetName,
    queryKey: ["spreadsheet-sheet", documentId, sheetName],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<SpreadsheetSheetData>
      >(
        `api/documents/${documentId}/spreadsheet/?sheet=${encodeURIComponent(
          sheetName as string,
        )}`,
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

/** "C12" -> { row: 12, col: 3 }; returns null for ranges/invalid refs. */
export const parseCellRef = (
  ref: string | null | undefined,
): { row: number; col: number } | null => {
  if (!ref) return null;
  const m = /^([A-Za-z]{1,3})(\d+)$/.exec(ref.trim());
  if (!m) return null;
  let col = 0;
  for (const ch of m[1].toUpperCase()) {
    col = col * 26 + (ch.charCodeAt(0) - 64);
  }
  return { row: parseInt(m[2], 10), col };
};

/** 1 -> "A", 27 -> "AA" */
export const colToLetter = (col: number): string => {
  let s = "";
  let n = col;
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};
